import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { KeyEventData } from '@innovation-os-ng/ui/chart-ng';
import { ClusterModel } from '../../model/model';

@Component({
  selector: 'signals-cluster-card',
  templateUrl: './cluster-card.component.html',
  styleUrls: ['./cluster-card.component.scss'],
  standalone: true,
  imports: [CommonModule]
})
export class ClusterCardComponent implements OnChanges {
  /**
   * Receives the cluster data.
   */
  @Input()
  public data: ClusterModel;

  /**
   * Receives the uri of cluster's that are currently opened.
   *
   * Needed so that we can make card active or inactive based on
   * opened stack.
   */
  @Input()
  public currentlyOpenedStack: Array<string> = [];

  /**
   * Receives the selected key event data.
   */
  @Input()
  public selectedKeyEvent: KeyEventData | null;

  /**
   * Provides the first cluster id of the selected key event.
   *
   * Note: So that we can scroll the first item only in the view
   * which is better in UX.
   */
  @Input()
  public firstClusterCardId: string = '';

  /**
   * Emits and notify the parent component about the click event.
   */
  @Output()
  public readonly clicked: EventEmitter<void> = new EventEmitter();

  /**
   * Active and selected state for the card.
   */
  public isActive: boolean = false;
  public isSelected: boolean = false;

  constructor(private readonly elementRef: ElementRef) {}

  /**
   * Listen to input changes.
   *
   * @param changes - Input changes
   */
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.selectedKeyEvent) {
      this.checkAndMarkActive();
    }

    if (changes?.currentlyOpenedStack) {
      this.checkSelectedCard();
    }
  }

  /**
   * Listen on click on the cluster card.
   */
  public onClick(): void {
    this.clicked.emit();
    this.isSelected = true;
  }

  /**
   * Check and mark the cluster active if selected key event
   * belongs to a cluster.
   */
  private checkAndMarkActive(): void {
    /**
     * If key event is not selected then the card is never active.
     */
    if (!this.selectedKeyEvent) {
      this.isActive = false;
      return;
    }

    /**
     * If cluster's key event id and the selected key event id match then
     * we know that cluster belong to selected key event.
     * So make it active.
     */
    this.isActive = this.selectedKeyEvent?.uri === this.data.keyEventId;

    // Scroll into view if not in view.
    this.scrollIntoView();
  }

  /**
   * Check whether the card is to be made active or not
   * based on currently opened cluster is stack.
   */
  private checkSelectedCard(): void {
    this.isSelected = this.currentlyOpenedStack.includes(this.data.clusterId);
  }

  /**
   * Scrolls the active cluster into view.
   */
  private scrollIntoView(): void {
    // If not active or not the first card in cluster, then don't scroll into the view.
    if (!this.isActive || this.firstClusterCardId !== this.data?.clusterId) {
      return;
    }

    // Scroll the card into view.
    const element: HTMLDivElement = this.elementRef.nativeElement;

    // If no element found then simple return.
    if (!element) {
      return;
    }

    // Scroll into view center vertically.
    element?.scrollIntoView({
      block: 'center'
    });

    /**
     * Scroll the list horizontally.
     *
     * Note: The same scrollIntoView inline property would do this as well
     * but there was UI issue when the new stack shifting feature was implemented.
     *
     * There was a UI issue as the stack resize and this scroll into view
     * would create abnormal UI issue. So, had to implement this way.
     */
    element?.parentElement?.scrollTo({ left: element?.offsetLeft });
  }
}
